import { useState, useEffect, useCallback } from 'react';
import { useGetProspects } from '../../../../api/services/lupp/prospect/prospect';
import type { ProspectSearchFilters } from '../../../../api/services/lupp/prospect/dto.prospect';

type UseProspectsTableParams = {
  enabled?: boolean;
};

/**
 * Custom hook for managing prospects table state (pagination, search, sort, filters)
 * @param params - Hook parameters
 * @param params.enabled - Whether the query should be enabled
 * @returns Table state and handlers
 */
export const useProspectsTable = ({ enabled = true }: UseProspectsTableParams = {}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(undefined);
  const [filters, setFilters] = useState<ProspectSearchFilters>({});

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      // Reset to first page when search changes
      if (searchQuery !== debouncedSearch) {
        setCurrentPage(0);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, debouncedSearch]);

  // Scroll to top when page changes
  useEffect(() => {
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage]);

  const {
    data: prospectsData,
    isLoading,
    isFetching,
    error,
  } = useGetProspects({
    page: currentPage,
    size: pageSize,
    searchString: debouncedSearch || undefined,
    sortField,
    sortOrder,
    ...filters,
    enabled,
  });

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleSort = useCallback((column: string, direction: 'asc' | 'desc') => {
    setSortField(column);
    setSortOrder(direction);
    setCurrentPage(0);
  }, []);

  const handleFiltersChange = useCallback((newFilters: ProspectSearchFilters) => {
    setFilters(newFilters);
    setCurrentPage(0);
  }, []);

  return {
    // Data
    data: prospectsData?.items || [],
    totalCount: prospectsData?.totalCount || 0,
    isLoading,
    isFetching,
    error,

    // Pagination state
    currentPage,
    pageSize,

    // Search state
    searchQuery,
    debouncedSearch,

    // Filter state
    filters,

    // Sort state
    sortField,
    sortOrder,

    // Handlers
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    onSearchChange: handleSearchChange,
    onSort: handleSort,
    onFiltersChange: handleFiltersChange,
  };
};
